import React, { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { API_BASE_URL } from "../apiConfig";
import "../Styles/SuperadminUsersPage.css";
import "../Styles/CompaniesPage.css"; 
import { Search, Users, AlertCircle, Loader, UserPlus, RefreshCw, X, Edit, Trash2 } from "lucide-react";
import Toast from "../Components/Common/Toast"; // Import Toast component

const SuperadminUsersPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { companyId, companyName } = location.state || {};

  const [users, setUsers] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [showAddUserDialog, setShowAddUserDialog] = useState(false);
  const [showEditUserDialog, setShowEditUserDialog] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [notification, setNotification] = useState({ type: '', message: '' });
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [userToDelete, setUserToDelete] = useState(null);

  const [userDetails, setUserDetails] = useState({
    name: "",
    email: "",
    employeeId: "",
    phoneNumber: "",
    address: "",
    role: "",
    password: "",
  });
  const [errorMessage, setErrorMessage] = useState("");

  useEffect(() => {
    const token = localStorage.getItem('idToken');
    if (!token) {
      navigate('/');
      return;
    }
    if (companyId) {
      fetchUsers(companyId, currentPage, searchQuery);
    } else {
      setLoading(false);
      setError("No company selected. Please select a company first.");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId, currentPage, navigate]);

  const showNotification = (type, message) => {
    setNotification({ type, message });
    setTimeout(() => setNotification({ type: '', message: '' }), 5000);
  };

  const fetchUsers = async (companyId, page = 1, query = "") => {
    if (!companyId) return;

    try {
      setLoading(true);
      const response = await fetch(
        `${API_BASE_URL}/users?companyId=${companyId}&page=${page}&search=${query}`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("idToken")}`,
          },
        }
      );
       if (response.status === 401 || response.status === 403) {
          localStorage.clear();
          window.location.href = '/';
          return;
        }
      const data = await response.json();
      if (data.success) {
        setUsers(data.users);
        setTotalPages(data.totalPages || 1);
        setError("");
      } else {
        setError(data.message || "Failed to fetch users");
        console.error("Failed to fetch users:", data.message);
      }
    } catch (err) {
      setError("An error occurred while fetching users");
      console.error("Error fetching users:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = () => {
    setCurrentPage(1);
    fetchUsers(companyId, 1, searchQuery);
  };

  const handleClear = () => {
    setSearchQuery(""); 
    setCurrentPage(1); 
    fetchUsers(companyId, 1, ""); 
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setUserDetails((prev) => ({
      ...prev,
      [name]: name === "role" ? value.toLowerCase() : value,
    }));
  };

  const handleEditUser = (user) => {
    setEditingUser(user);
    setUserDetails({
      name: user.name || "",
      email: user.email || "",
      employeeId: user.employeeId || "",
      phoneNumber: user.phoneNumber || "",
      address: user.address || "",
      role: user.role || "",
      password: "", 
    });
    setShowEditUserDialog(true);
  };

  const handleUpdateUser = async () => {
    if (!companyId || !editingUser) {
      setErrorMessage("No company or user selected.");
      return;
    }
    const { name, email, employeeId, phoneNumber, address, role, password } = userDetails;
    // Validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!name.trim() || name.length < 2 || name.length > 100) {
      setErrorMessage("Name must be 2-100 characters and not just spaces.");
      return;
    }
    if (!emailRegex.test(email)) {
      setErrorMessage("Please enter a valid email address.");
      return;
    }
    if (!/^[A-Za-z0-9]+$/.test(employeeId)) {
      setErrorMessage("Employee ID must be alphanumeric and have no spaces.");
      return;
    }
    if (!/^\d{7,15}$/.test(phoneNumber)) {
      setErrorMessage("Please enter a valid phone number (7-15 digits).");
      return;
    }
    if (!address.trim() || address.length < 5 || address.length > 200) {
      setErrorMessage("Address must be 5-200 characters and not just spaces.");
      return;
    }
    if (!["user", "admin"].includes(role.toLowerCase())) {
      setErrorMessage("Role must be 'user' or 'admin'.");
      return;
    }
    if (password && (password.length < 8 || !/\d/.test(password) || !/[A-Za-z]/.test(password))) {
      setErrorMessage("Password must be at least 8 characters and contain letters and numbers.");
      return;
    }
    try {
      const response = await fetch(`${API_BASE_URL}/users/${editingUser.id}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("idToken")}`,
        },
        body: JSON.stringify({
          companyId,
          name,
          email,
          employeeId,
          phoneNumber,
          address,
          role,
          ...(userDetails.password ? { password: userDetails.password } : {})
        }),
      });
      
      const data = await response.json();
      if (data.success) {
        showNotification('success', "User updated successfully!");
        setUserDetails({
          name: "",
          email: "",
          employeeId: "",
          phoneNumber: "",
          address: "",
          role: "",
          password: "",
        });
        setErrorMessage("");
        setShowEditUserDialog(false);
        setEditingUser(null);
        fetchUsers(companyId, currentPage, searchQuery);
      } else {
        setErrorMessage(data.message || "Failed to update user.");
      }
    } catch (err) {
      console.error("Error updating user:", err);
      setErrorMessage("An error occurred. Please try again.");
    }
  };

  const handleDeleteUser = (userId) => {
    setUserToDelete(userId);
    setShowDeleteConfirm(true);
  };

  const confirmDeleteUser = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/users/${userToDelete}/delete`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("idToken")}`,
        }
      });
      
      const data = await response.json();
      if (data.success) {
        showNotification('success', "User deleted successfully!");
        setUsers(users.filter(user => user.id !== userToDelete));
        
        if (users.length === 1 && currentPage > 1) {
          setCurrentPage(currentPage - 1);
        } else {
          fetchUsers(companyId, currentPage, searchQuery);
        }
      } else {
        showNotification('error', data.message || "Failed to delete user.");
      }
    } catch (err) {
      console.error("Error deleting user:", err);
      showNotification('error', "An error occurred while deleting the user.");
    } finally {
      setShowDeleteConfirm(false);
      setUserToDelete(null);
    }
  };

  const cancelDeleteUser = () => {
    setShowDeleteConfirm(false);
    setUserToDelete(null);
  };

  const closeEditDialog = () => {
    setShowEditUserDialog(false);
    setEditingUser(null);
    setUserDetails({
      name: "",
      email: "",
      employeeId: "",
      phoneNumber: "",
      address: "",
      role: "",
      password: "",
    });
    setErrorMessage("");
  };

  const handleAddUser = async () => {
    if (!companyId) {
      setErrorMessage("No company selected.");
      return;
    }

    const { name, email, employeeId, phoneNumber, address, role, password } = userDetails;
    // Validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!name.trim() || name.length < 2 || name.length > 100) {
      setErrorMessage("Name must be 2-100 characters and not just spaces.");
      return;
    }
    if (!emailRegex.test(email)) {
      setErrorMessage("Please enter a valid email address.");
      return;
    }
    if (!/^[A-Za-z0-9]+$/.test(employeeId)) {
      setErrorMessage("Employee ID must be alphanumeric and have no spaces.");
      return;
    }
    if (!/^\d{7,15}$/.test(phoneNumber)) {
      setErrorMessage("Please enter a valid phone number (7-15 digits).");
      return;
    }
    if (!address.trim() || address.length < 5 || address.length > 200) {
      setErrorMessage("Address must be 5-200 characters and not just spaces.");
      return;
    }
    if (!["user", "admin"].includes(role.toLowerCase())) {
      setErrorMessage("Role must be 'user' or 'admin'.");
      return;
    }
    if (password.length < 8 || !/\d/.test(password) || !/[A-Za-z]/.test(password)) {
      setErrorMessage("Password must be at least 8 characters and contain letters and numbers.");
      return;
    }
    try {
      const response = await fetch(`${API_BASE_URL}/companies/create-user`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("idToken")}`,
        },
        body: JSON.stringify({
          companyId,
          name,
          email,
          employeeId,
          phoneNumber,
          address,
          role,
          password,
        }),
      });
      const data = await response.json();
      if (data.success) {
        showNotification('success', "User added successfully!");
        setUserDetails({
          name: "",
          email: "",
          employeeId: "",
          phoneNumber: "",
          address: "",
          role: "",
          password: "",
        });
        setErrorMessage(""); 
        setShowAddUserDialog(false); 
        
        fetchUsers(companyId, currentPage, searchQuery);
      } else {
        setErrorMessage(data.message || "Failed to add user."); 
      }
    } catch (err) {
      console.error("Error adding user:", err);
      setErrorMessage("An error occurred. Please try again."); 
    }
  };

  const closeDialog = () => {
    setShowAddUserDialog(false);
    setUserDetails({
      name: "",
      email: "",
      employeeId: "",
      phoneNumber: "",
      address: "",
      role: "",
      password: "",
    });
    setErrorMessage("");
  };

  return (
    <div className="su-page-container">
      <div className="su-page-content">
        {/* Toast notification */}
        <Toast 
          type={notification.type} 
          message={notification.message} 
          onClose={() => setNotification({ type: '', message: '' })} 

        />

        {/* Delete confirmation dialog */}
        {showDeleteConfirm && (
          <div className="su-dialog-overlay">
            <div className="su-delete-dialog">
              <div className="dialog-header">
                <h2>Confirm Delete</h2>
                <button className="dialog-close-btn" onClick={cancelDeleteUser}>
                  <X size={20} />
                </button>
              </div>
              <div className="dialog-content">
                <p>Are you sure you want to remove this user?</p>
                <p className="su-warning-text">This action cannot be undone.</p>
              </div>
              <div className="dialog-buttons">
                <button
                  type="button"
                  className="cancel-button"
                  onClick={cancelDeleteUser}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="su-delete-button"
                  onClick={confirmDeleteUser}
                >
                  Delete
                </button>
              </div>
            </div>
          </div>
        )}

        <div className="su-header">
          <div className="su-header-icon">
            <Users size={28} color="#f3f4f6" />
          </div>
          <div className="su-header-text">
            <h1>Manage Users</h1>
            {companyName && (
              <p className="su-company-name">
                {companyName}
              </p>
            )}
          </div>
        </div>

        <div className="su-actions-bar">
          <div className="su-search-section">
            <div className="su-search-container">
              <Search className="su-search-icon" size={18} />
              <input
                type="text"
                placeholder="Search by name, email, or employee ID"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onKeyPress={handleKeyPress}
                className="su-search-input"
              />
            </div>
            <div className="su-button-group">
              <button className="su-search-button" onClick={handleSearch}>
                Search
              </button>
              <button className="su-clear-button" onClick={handleClear}>
                Clear
              </button>
            </div>
          </div>
          
          <button 
            className="su-add-user-button" 
            onClick={() => setShowAddUserDialog(true)}
          >
            <UserPlus size={18} />
            Add User
          </button>
        </div>

        {loading ? (
          <div className="su-loading-container">
            <Loader size={40} className="su-loading-spinner" />
            <p>Loading users...</p>
          </div>
        ) : error ? (
          <div className="su-error-container">
            <AlertCircle size={40} color="#ef4444" />
            <h2>Error</h2>
            <p>{error}</p>
            <button 
              className="su-retry-button"
              onClick={() => fetchUsers(companyId, currentPage, searchQuery)}
            >
              <RefreshCw size={16} />
              Retry
            </button>
          </div>
        ) : (
          <>
            {users.length > 0 ? (
              <div className="su-table-card">
                <div className="su-table-container">
                  <table className="su-users-table">
                    <thead>
                      <tr>
                        <th>Name</th>
                        <th>Email</th>
                        <th>Employee ID</th>
                        <th>Phone Number</th>
                        <th>Address</th>
                        <th>Role</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {users.map((user) => (
                        <tr key={user.id} className="su-table-row">
                          <td className="su-name-cell">
                            <div className="su-user-avatar">
                              {user.name.charAt(0).toUpperCase()}
                            </div>
                            {user.name}
                          </td>
                          <td>{user.email}</td>
                          <td>{user.employeeId || "N/A"}</td>
                          <td>{user.phoneNumber || "N/A"}</td>
                          <td>{user.address || "N/A"}</td>
                          <td>
                            <span className={`su-role-badge ${user.role === 'admin' ? 'su-role-admin' : 'su-role-user'}`}>
                              {user.role || "N/A"}
                            </span>
                          </td>
                          <td className="su-actions-cell">
                            <div className="su-actions-buttons">
                              <button 
                                className="su-action-btn su-edit-btn" 
                                onClick={() => handleEditUser(user)}
                                title="Edit User"
                              >
                                <Edit size={16} />
                              </button>
                              <button 
                                className="su-action-btn su-delete-btn" 
                                onClick={() => handleDeleteUser(user.id)}
                                title="Remove User"
                              >
                                <Trash2 size={16} />
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {totalPages > 1 && (
                  <div className="su-pagination">
                    <button 
                      className="su-pagination-arrow" 
                      onClick={() => handlePageChange(Math.max(1, currentPage - 1))}
                      disabled={currentPage === 1}
                    >
                      &laquo;
                    </button>
                    
                    {Array.from({ length: totalPages }, (_, index) => {
                      const pageNum = index + 1;
                      if (
                        pageNum === 1 || 
                        pageNum === totalPages || 
                        (pageNum >= currentPage - 1 && pageNum <= currentPage + 1)
                      ) {
                        return (
                          <button
                            key={pageNum}
                            className={currentPage === pageNum ? "su-pagination-active" : ""}
                            onClick={() => handlePageChange(pageNum)}
                          >
                            {pageNum}
                          </button>
                        );
                      } else if (
                        pageNum === currentPage - 2 || 
                        pageNum === currentPage + 2
                      ) {
                        return <span key={pageNum} className="su-pagination-ellipsis">...</span>;
                      }
                      return null;
                    })}
                    
                    <button 
                      className="su-pagination-arrow" 
                      onClick={() => handlePageChange(Math.min(totalPages, currentPage + 1))}
                      disabled={currentPage === totalPages}
                    >
                      &raquo;
                    </button>
                  </div>
                )}
              </div>
            ) : (
              <div className="su-empty-state">
                <UserPlus size={48} color="#6b7280" />
                <h2>No users found</h2>
                <p>There are no users matching your search criteria</p>
                <button 
                  className="su-add-user-button-empty" 
                  onClick={() => setShowAddUserDialog(true)}
                >
                  <UserPlus size={18} />
                  Add User
                </button>
              </div>
            )}
          </>
        )}

        {/* Add User Dialog */}
        {showAddUserDialog && (
          <div className="su-dialog-overlay">
            <div className="companies-page-container dialog-mode">
              <div className="companies-page-content">
                <div className="dialog-header">
                  <h1>Add User to Company</h1>
                  <button className="dialog-close-btn" onClick={closeDialog}>
                    <X size={20} />
                  </button>
                </div>

                {/* Display Error Message */}
                {errorMessage && <div className="error-message">{errorMessage}</div>}

                {/* Display Selected Company */}
                <div className="selected-company-section">
                  <label className="company-label">Company Name:</label>
                  <p className="company-name">{companyName || "No company selected"}</p>
                  <hr className="divider" />
                </div>

                {/* Add User Form */}
                <div className="add-user-form">
                  <h2>User Details</h2>
                  <input
                    type="text"
                    name="name"
                    placeholder="Name"
                    value={userDetails.name}
                    onChange={handleInputChange}
                    required
                  />
                  <input
                    type="email"
                    name="email"
                    placeholder="Email"
                    value={userDetails.email}
                    onChange={handleInputChange}
                    required
                  />
                  <input
                    type="text"
                    name="employeeId"
                    placeholder="Employee ID"
                    value={userDetails.employeeId}
                    onChange={handleInputChange}
                    required
                  />
                  <input
                    type="text"
                    name="phoneNumber"
                    placeholder="Phone Number"
                    value={userDetails.phoneNumber}
                    onChange={handleInputChange}
                    required
                  />
                  <input
                    type="text"
                    name="address"
                    placeholder="Address"
                    value={userDetails.address}
                    onChange={handleInputChange}
                    required
                  />
                  <input
                    type="text"
                    name="role"
                    placeholder="Role (user/admin)"
                    value={userDetails.role}
                    onChange={handleInputChange}
                    required
                  />
                  <input
                    type="password"
                    name="password"
                    placeholder="Password"
                    value={userDetails.password}
                    onChange={handleInputChange}
                    required
                  />
                  <div className="dialog-buttons">
                    <button className="cancel-button" onClick={closeDialog}>Cancel</button>
                    <button className="add-button" onClick={handleAddUser}>Add User</button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Edit User Dialog */}
        {showEditUserDialog && (
          <div className="su-dialog-overlay">
            <div className="companies-page-container dialog-mode">
              <div className="companies-page-content">
                <div className="dialog-header">
                  <h1>Edit User</h1>
                  <button className="dialog-close-btn" onClick={closeEditDialog}>
                    <X size={20} />
                  </button>
                </div>

                {/* Display Error Message */}
                {errorMessage && <div className="error-message">{errorMessage}</div>}

                {/* Display Selected Company */}
                <div className="selected-company-section">
                  <label className="company-label">Company Name:</label>
                  <p className="company-name">{companyName || "No company selected"}</p>
                  <hr className="divider" />
                </div>

                {/* Edit User Form */}
                <div className="add-user-form">
                  <h2>User Details</h2>
                  <input
                    type="text"
                    name="name"
                    placeholder="Name"
                    value={userDetails.name}
                    onChange={handleInputChange}
                    required
                  />
                  <input
                    type="email"
                    name="email"
                    placeholder="Email"
                    value={userDetails.email}
                    onChange={handleInputChange}
                    required
                  />
                  <input
                    type="text"
                    name="employeeId"
                    placeholder="Employee ID"
                    value={userDetails.employeeId}
                    onChange={handleInputChange}
                    required
                  />
                  <input
                    type="text"
                    name="phoneNumber"
                    placeholder="Phone Number"
                    value={userDetails.phoneNumber}
                    onChange={handleInputChange}
                    required
                  />
                  <input
                    type="text"
                    name="address"
                    placeholder="Address"
                    value={userDetails.address}
                    onChange={handleInputChange}
                    required
                  />
                  <input
                    type="text"
                    name="role"
                    placeholder="Role (user/admin)"
                    value={userDetails.role}
                    onChange={handleInputChange}
                    required
                  />
                  <input
                    type="password"
                    name="password"
                    placeholder="New Password (leave blank to keep current)"
                    value={userDetails.password}
                    onChange={handleInputChange}
                  />
                  <div className="dialog-buttons">
                    <button className="cancel-button" onClick={closeEditDialog}>Cancel</button>
                    <button className="add-button" onClick={handleUpdateUser}>Update User</button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default SuperadminUsersPage;
